import { UploadedFileMeta } from "src/store/api/generatedApi";
import { FileManagerFile } from "./types";

/**
 * Converts uploaded files from the API to file manager format
 */
export const convertUploadedFilesToFileManager = (
  uploadedFiles: UploadedFileMeta[]
): FileManagerFile[] => {
  return uploadedFiles.map((file: UploadedFileMeta) => ({
    name: file.file.name,
    isDirectory: false,
    path: `/${file.file.name}`,
    updatedAt: file.uploaded_at,
    size: undefined, // Size not provided in the API response
    id: file.id,
    type: file.type,
    uploadedBy: file.uploaded_by_id,
  }));
};

/**
 * Decodes base64 content to binary data
 */
export const decodeBase64ToBytes = (base64Content: string): Uint8Array => {
  const binaryString = atob(base64Content);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
};

/**
 * Creates and triggers a file download in the browser
 */
export const triggerFileDownload = (
  bytes: Uint8Array,
  fileName: string
): void => {
  const blob = new Blob([bytes as BlobPart]);
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Processes a downloaded file: decodes base64 and triggers download
 */
export const processDownloadedFile = (
  base64Content: string,
  fileName: string
): void => {
  const bytes = decodeBase64ToBytes(base64Content);
  triggerFileDownload(bytes, fileName);
};

/**
 * Creates a FileList object from an array of File objects for drag-and-drop
 */
export const createFileListFromFiles = (files: File[]): FileList => {
  return {
    0: files[0],
    length: files.length,
    item: (index: number) => files[index] ?? null,
    [Symbol.iterator]: function* () {
      for (const file of files) {
        yield file;
      }
    },
  } as FileList;
};

/**
 * Handles file upload with a delay for backend processing
 */
export const uploadFilesWithDelay = async (
  files: FileList,
  uploadFn: (file: File) => Promise<void>,
  refetchFn: () => Promise<void>,
  delay = 1000
): Promise<void> => {
  for (const file of files) {
    await uploadFn(file);
  }
  // Refresh the file list after upload
  setTimeout(() => {
    void refetchFn();
  }, delay);
};

/**
 * Creates a file drop handler for drag and drop functionality
 */
export const createDropHandler = (
  onFileUpload: (files: FileList) => Promise<void>
) => {
  return (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const fileList = createFileListFromFiles(acceptedFiles);
      void onFileUpload(fileList);
    }
  };
};
